/* SMS compose dialog — desktop-safe alternative to a raw sms: deep-link.
   Shows the pre-composed message + tracking QR + Copy/Open buttons so the
   dispatcher can either paste it wherever they normally SMS customers from,
   or (on mobile) tap "Open SMS app" to fire the native SMS composer.

   Props:
     open, onOpenChange           standard shadcn dialog controls
     phone (string, editable)     initial recipient number
     message (string, editable)   initial pre-composed message
     trackingUrl (string)         plain link, rendered as a small QR
     customerName (string)        shown in the header for context
*/
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Copy, MessageSquare, Link as LinkIcon, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function SmsComposeDialog({
  open,
  onOpenChange,
  phone: initialPhone = "",
  message: initialMessage = "",
  trackingUrl = "",
  customerName = "",
}) {
  const [phone, setPhone] = useState(initialPhone);
  const [message, setMessage] = useState(initialMessage);

  useEffect(() => { if (open) { setPhone(initialPhone); setMessage(initialMessage); } }, [open, initialPhone, initialMessage]);

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard blocked · long-press the field to copy manually");
    }
  };

  const openNativeSms = () => {
    const cleanPhone = (phone || "").replace(/\s+/g, "");
    if (!cleanPhone) { toast.error("Add a phone number first"); return; }
    // sms:PHONE?&body=... — the extra & after ? keeps iOS happy on older versions
    const url = `sms:${cleanPhone}?&body=${encodeURIComponent(message || "")}`;
    // Desktop browsers silently ignore this. Also give the user the copied
    // message as a fallback so they can paste it into whichever tool they use.
    window.location.href = url;
    toast.info("Opening SMS app · if nothing happens you're on desktop — use the Copy buttons instead");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="sms-compose-dialog"
        className="rounded-none bg-surface border-industrial max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="heading uppercase flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary-orange" />
            SMS Tracking Link
            {customerName && (
              <span className="mono text-xs text-muted-industrial normal-case">· {customerName}</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-industrial mono uppercase tracking-widest">
            On mobile · tap "Open SMS app". On desktop · use Copy and paste into your own SMS tool.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[1fr_auto] gap-4">
          <div className="space-y-3 min-w-0">
            <div>
              <Label className="label-tag">To</Label>
              <Input
                data-testid="sms-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Recipient phone number"
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
            <div>
              <Label className="label-tag">Message</Label>
              <Textarea
                data-testid="sms-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                className="rounded-none bg-transparent border-industrial mono text-xs"
              />
              <div className="mono text-[10px] text-muted-industrial mt-1">
                {message.length} chars · {Math.max(1, Math.ceil(message.length / 160))} SMS segment(s)
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-start pt-6">
            {trackingUrl && (
              <>
                <div className="bg-white p-2 border border-industrial">
                  <QRCodeSVG value={trackingUrl} size={110} level="M" includeMargin={false} />
                </div>
                <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mt-2 text-center max-w-[130px]">
                  Customer scans to open tracker
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-industrial">
          <Button
            data-testid="sms-copy-msg-btn"
            variant="outline"
            onClick={() => copy(message, "Message")}
            className="rounded-none bg-transparent border-industrial hover:border-primary-orange hover:text-primary-orange uppercase text-xs tracking-widest"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Message
          </Button>
          <Button
            data-testid="sms-copy-link-btn"
            variant="outline"
            onClick={() => copy(trackingUrl, "Tracking link")}
            className="rounded-none bg-transparent border-industrial hover:border-primary-orange hover:text-primary-orange uppercase text-xs tracking-widest"
          >
            <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> Copy Link
          </Button>
          <Button
            data-testid="sms-copy-phone-btn"
            variant="outline"
            onClick={() => copy(phone, "Phone")}
            className="rounded-none bg-transparent border-industrial hover:border-primary-orange hover:text-primary-orange uppercase text-xs tracking-widest"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Phone
          </Button>
          <Button
            data-testid="sms-open-native-btn"
            onClick={openNativeSms}
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-xs tracking-widest font-bold"
          >
            <Smartphone className="w-3.5 h-3.5 mr-1.5" /> Open SMS App
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
